import React, { useCallback, useEffect, useState } from 'react';
import api from '../utils/api';
import { useNotification } from './Notification';
import { formatUsdAmount } from '../utils/money';
import { useWallet } from '../context/WalletContext';
import {
  commitNftRewardSnapshot,
  finalizeNftRewardDistribution,
  getNftRewardPoolSnapshot,
} from '../utils/nftRewardChain';
import { syncChainConfigFromServer } from '../utils/syncChainConfig';

const EMPTY_COLLECTION = {
  name: '',
  contractAddress: '',
  tokenStandard: 'erc721',
  weightMode: 'per_token',
  powerFeedUrl: '',
  defaultPoints: 1,
  tokenDecimals: 18,
  enabled: true,
  tiers: [
    { name: 'Bronze', points: 1, minHoldings: 1 },
    { name: 'Silver', points: 2, minHoldings: 2 },
    { name: 'Gold', points: 5, minHoldings: 5 },
    { name: 'Diamond', points: 10, minHoldings: 10 },
  ],
};

const AdminNftRewardsTab = () => {
  const { showNotification } = useNotification();
  const { ensureConnected } = useWallet();
  const [stats, setStats] = useState(null);
  const [collections, setCollections] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [distributions, setDistributions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [pool, setPool] = useState(null);
  const [form, setForm] = useState(EMPTY_COLLECTION);
  const [editingId, setEditingId] = useState(null);
  const [distAmount, setDistAmount] = useState('');
  const [distLabel, setDistLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [subTab, setSubTab] = useState('overview');

  const refresh = useCallback(async () => {
    try {
      await syncChainConfigFromServer();
      const [s, c, sn, d, st] = await Promise.all([
        api.get('/nft-rewards/admin/stats'),
        api.get('/nft-rewards/admin/collections'),
        api.get('/nft-rewards/admin/snapshots'),
        api.get('/nft-rewards/admin/distributions'),
        api.get('/nft-rewards/admin/settings'),
      ]);
      setStats(s.data);
      setCollections(c.data.collections || []);
      setSnapshots(sn.data.snapshots || []);
      setDistributions(d.data.distributions || []);
      setSettings(st.data.settings);
      setPool(await getNftRewardPoolSnapshot());
    } catch (e) {
      showNotification(e?.response?.data?.message || e.message || 'Failed to load', 'error');
    }
  }, [showNotification]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const saveCollection = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/nft-rewards/admin/collections/${editingId}`, form);
      } else {
        await api.post('/nft-rewards/admin/collections', form);
      }
      showNotification('Collection saved', 'success');
      setForm(EMPTY_COLLECTION);
      setEditingId(null);
      await refresh();
    } catch (err) {
      showNotification(err?.response?.data?.message || 'Save failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const runSnapshot = async () => {
    setBusy(true);
    try {
      await ensureConnected();
      const { data } = await api.post('/nft-rewards/admin/snapshots/run', {});
      showNotification(
        `Snapshot created (${data.snapshot.totalPoints} pts). Committing on-chain…`,
        'info'
      );
      const txHash = await commitNftRewardSnapshot(data.commitParams);
      await api.post(`/nft-rewards/admin/snapshots/${data.snapshot._id}/commit-confirm`, { txHash });
      showNotification(`Snapshot committed: ${txHash.slice(0, 10)}…`, 'success');
      await refresh();
    } catch (err) {
      showNotification(err?.response?.data?.message || err.message || 'Snapshot failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const createAndFinalizeDistribution = async () => {
    setBusy(true);
    try {
      await ensureConnected();
      const { data } = await api.post('/nft-rewards/admin/distributions', {
        amountUsd: Number(distAmount),
        label: distLabel,
      });
      showNotification('Distribution prepared. Finalizing on-chain…', 'info');
      const txHash = await finalizeNftRewardDistribution(data.finalizeParams);
      await api.post(`/nft-rewards/admin/distributions/${data.distribution._id}/finalize-confirm`, {
        txHash,
      });
      showNotification(`Distributed ${formatUsdAmount(data.distribution.totalAmountUsd)}`, 'success');
      setDistAmount('');
      setDistLabel('');
      await refresh();
    } catch (err) {
      showNotification(err?.response?.data?.message || err.message || 'Distribution failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const commitPendingSnapshot = async (snap) => {
    setBusy(true);
    try {
      await ensureConnected();
      const txHash = await commitNftRewardSnapshot({
        snapshotId: snap.chainSnapshotId,
        contentHash: snap.contentHash,
        totalPoints: snap.totalPoints,
        snapshotAt: Math.floor(new Date(snap.snapshotAt).getTime() / 1000),
      });
      await api.post(`/nft-rewards/admin/snapshots/${snap._id}/commit-confirm`, { txHash });
      showNotification('Snapshot committed', 'success');
      await refresh();
    } catch (err) {
      showNotification(err?.response?.data?.message || err.message || 'Commit failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const saveSettings = async () => {
    setBusy(true);
    try {
      await api.put('/nft-rewards/admin/settings', {
        nextSnapshotAt: settings?.nextSnapshotAt || null,
        snapshotCronEnabled: settings?.snapshotCronEnabled !== false,
      });
      showNotification('Settings saved', 'success');
      await refresh();
    } catch (err) {
      showNotification(err?.response?.data?.message || 'Failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {['overview', 'collections', 'snapshots', 'distribute'].map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setSubTab(t)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium capitalize ${
              subTab === t
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
            }`}
          >
            {t}
          </button>
        ))}
        <button
          type="button"
          onClick={refresh}
          className="ml-auto text-sm text-blue-600 hover:underline"
        >
          Refresh
        </button>
      </div>

      {subTab === 'overview' && (
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { l: 'Open points', v: stats?.openPoints ?? '—' },
              { l: 'Open holders', v: stats?.openHolders ?? '—' },
              { l: 'Distributed', v: formatUsdAmount(stats?.totalDistributedUsd || 0) },
              { l: 'Unclaimed', v: formatUsdAmount(stats?.totalUnclaimedUsd || 0) },
            ].map((x) => (
              <div key={x.l} className="rounded-xl bg-white dark:bg-gray-800 p-4 shadow border border-gray-100 dark:border-gray-700">
                <div className="text-xs text-gray-500 uppercase">{x.l}</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{x.v}</div>
              </div>
            ))}
          </div>
          {pool && (
            <div className="rounded-xl bg-white dark:bg-gray-800 p-4 shadow text-sm">
              <div className="font-semibold mb-2">On-chain pool</div>
              <div className="grid sm:grid-cols-4 gap-2 text-gray-600 dark:text-gray-300">
                <div>Funded: {formatUsdAmount(pool.totalFunded)}</div>
                <div>Allocated: {formatUsdAmount(pool.totalAllocated)}</div>
                <div>Claimed: {formatUsdAmount(pool.totalClaimed)}</div>
                <div>Available: {formatUsdAmount(pool.availableToAllocate)}</div>
              </div>
            </div>
          )}
          <div className="rounded-xl bg-white dark:bg-gray-800 p-4 shadow">
            <div className="font-semibold mb-2">Schedule</div>
            <label className="block text-sm mb-1">Next snapshot (UTC)</label>
            <input
              type="datetime-local"
              className="border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
              value={
                settings?.nextSnapshotAt
                  ? new Date(settings.nextSnapshotAt).toISOString().slice(0, 16)
                  : ''
              }
              onChange={(e) =>
                setSettings((s) => ({
                  ...s,
                  nextSnapshotAt: e.target.value ? new Date(e.target.value + 'Z').toISOString() : null,
                }))
              }
            />
            <label className="flex items-center gap-2 mt-3 text-sm">
              <input
                type="checkbox"
                checked={settings?.snapshotCronEnabled !== false}
                onChange={(e) =>
                  setSettings((s) => ({ ...s, snapshotCronEnabled: e.target.checked }))
                }
              />
              Auto-run scheduled snapshots
            </label>
            <button
              type="button"
              disabled={busy}
              onClick={saveSettings}
              className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm"
            >
              Save schedule
            </button>
          </div>
          <div className="rounded-xl bg-white dark:bg-gray-800 p-4 shadow">
            <div className="font-semibold mb-2">Top open-point holders</div>
            <div className="max-h-64 overflow-auto text-sm">
              {(stats?.topHolders || []).map((h) => (
                <div key={h.walletAddress} className="flex justify-between py-1 border-b border-gray-100 dark:border-gray-700">
                  <span className="font-mono text-xs">{h.walletAddress}</span>
                  <span className="font-semibold">{h.points} pts</span>
                </div>
              ))}
              {!stats?.topHolders?.length && <p className="text-gray-500">No open points yet.</p>}
            </div>
          </div>
        </div>
      )}

      {subTab === 'collections' && (
        <div className="grid lg:grid-cols-2 gap-6">
          <form onSubmit={saveCollection} className="rounded-xl bg-white dark:bg-gray-800 p-4 shadow space-y-3">
            <h3 className="font-semibold">{editingId ? 'Edit collection' : 'Add collection'}</h3>
            <input
              required
              placeholder="Name"
              className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <input
              required
              placeholder="Contract address"
              className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600 font-mono text-sm"
              value={form.contractAddress}
              onChange={(e) => setForm({ ...form, contractAddress: e.target.value })}
            />
            <div className="grid grid-cols-2 gap-2">
              <select
                className="border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
                value={form.tokenStandard}
                onChange={(e) => setForm({ ...form, tokenStandard: e.target.value })}
              >
                {['erc721', 'erc1155', 'erc20', 'erc6551', 'auto'].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <select
                className="border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
                value={form.weightMode}
                onChange={(e) => setForm({ ...form, weightMode: e.target.value })}
              >
                <option value="per_token">per_token (pts × balance)</option>
                <option value="count_ladder">count_ladder (tier by count)</option>
                <option value="erc20_range">erc20_range</option>
                <option value="token_map">token_map (tokenId → tier)</option>
                <option value="partner_power">partner_power (Player Power × pts)</option>
              </select>
            </div>
            {form.weightMode === 'partner_power' && (
              <div className="space-y-1">
                <input
                  required
                  placeholder="Power feed URL, e.g. https://api.werrobin.io/api/integrations/wergame/power"
                  className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600 font-mono text-sm"
                  value={form.powerFeedUrl || ''}
                  onChange={(e) => setForm({ ...form, powerFeedUrl: e.target.value })}
                />
                <p className="text-xs text-gray-500">
                  Points = holder&apos;s total Player Power × Default pts (e.g. 100 → a 1.35x Player earns 135).
                  The server reads the feed with WERROBIN_INTEGRATION_KEY. Tiers are ignored in this mode.
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <label className="text-sm">
                Default pts
                <input
                  type="number"
                  className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
                  value={form.defaultPoints}
                  onChange={(e) => setForm({ ...form, defaultPoints: Number(e.target.value) })}
                />
              </label>
              <label className="text-sm">
                ERC-20 decimals
                <input
                  type="number"
                  className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
                  value={form.tokenDecimals}
                  onChange={(e) => setForm({ ...form, tokenDecimals: Number(e.target.value) })}
                />
              </label>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium">Tiers</div>
              {(form.tiers || []).map((t, i) => (
                <div key={i} className="grid grid-cols-4 gap-1">
                  <input
                    placeholder="Name"
                    className="border rounded px-2 py-1 text-sm dark:bg-gray-900"
                    value={t.name}
                    onChange={(e) => {
                      const tiers = [...form.tiers];
                      tiers[i] = { ...tiers[i], name: e.target.value };
                      setForm({ ...form, tiers });
                    }}
                  />
                  <input
                    type="number"
                    placeholder="Pts"
                    className="border rounded px-2 py-1 text-sm dark:bg-gray-900"
                    value={t.points}
                    onChange={(e) => {
                      const tiers = [...form.tiers];
                      tiers[i] = { ...tiers[i], points: Number(e.target.value) };
                      setForm({ ...form, tiers });
                    }}
                  />
                  <input
                    type="number"
                    placeholder="Min"
                    className="border rounded px-2 py-1 text-sm dark:bg-gray-900"
                    value={t.minHoldings}
                    onChange={(e) => {
                      const tiers = [...form.tiers];
                      tiers[i] = { ...tiers[i], minHoldings: Number(e.target.value) };
                      setForm({ ...form, tiers });
                    }}
                  />
                  <input
                    placeholder="tokenIds comma"
                    className="border rounded px-2 py-1 text-sm dark:bg-gray-900"
                    value={(t.tokenIds || []).join(',')}
                    onChange={(e) => {
                      const tiers = [...form.tiers];
                      tiers[i] = {
                        ...tiers[i],
                        tokenIds: e.target.value.split(',').map((x) => x.trim()).filter(Boolean),
                      };
                      setForm({ ...form, tiers });
                    }}
                  />
                </div>
              ))}
              <button
                type="button"
                className="text-xs text-blue-600"
                onClick={() =>
                  setForm({
                    ...form,
                    tiers: [...(form.tiers || []), { name: 'Tier', points: 1, minHoldings: 1, tokenIds: [] }],
                  })
                }
              >
                + tier
              </button>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={busy}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm"
              >
                {editingId ? 'Update' : 'Create'}
              </button>
              {editingId && (
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-300 rounded-lg text-sm"
                  onClick={() => {
                    setEditingId(null);
                    setForm(EMPTY_COLLECTION);
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
          <div className="space-y-2">
            {collections.map((c) => (
              <div key={c.id} className="rounded-xl bg-white dark:bg-gray-800 p-4 shadow flex justify-between gap-3">
                <div>
                  <div className="font-semibold">{c.name}</div>
                  <div className="text-xs font-mono text-gray-500">{c.contractAddress}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {c.tokenStandard} · {c.weightMode} · {c.enabled ? 'on' : 'off'}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    type="button"
                    className="text-xs text-blue-600"
                    onClick={() => {
                      setEditingId(c.id);
                      setForm({ ...EMPTY_COLLECTION, ...c });
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="text-xs text-red-600"
                    onClick={async () => {
                      await api.delete(`/nft-rewards/admin/collections/${c.id}`);
                      refresh();
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {subTab === 'snapshots' && (
        <div className="space-y-4">
          <button
            type="button"
            disabled={busy}
            onClick={runSnapshot}
            className="px-4 py-2 bg-amber-500 text-slate-950 font-semibold rounded-lg text-sm"
          >
            Run snapshot now + commit on-chain
          </button>
          <div className="rounded-xl bg-white dark:bg-gray-800 shadow overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left border-b dark:border-gray-700">
                  <th className="p-3">ID</th>
                  <th className="p-3">When</th>
                  <th className="p-3">Points</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {snapshots.map((s) => (
                  <tr key={s._id} className="border-b dark:border-gray-700">
                    <td className="p-3">#{s.chainSnapshotId}</td>
                    <td className="p-3">{new Date(s.snapshotAt).toISOString()}</td>
                    <td className="p-3">{s.totalPoints}</td>
                    <td className="p-3">{s.status}</td>
                    <td className="p-3">
                      {s.status === 'pending_commit' && (
                        <button
                          type="button"
                          disabled={busy}
                          className="text-blue-600 text-xs"
                          onClick={() => commitPendingSnapshot(s)}
                        >
                          Commit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {subTab === 'distribute' && (
        <div className="space-y-4 max-w-xl">
          <div className="rounded-xl bg-white dark:bg-gray-800 p-4 shadow space-y-3">
            <h3 className="font-semibold">Distribute USDC across open points</h3>
            <p className="text-xs text-gray-500">
              Open points: <strong>{stats?.openPoints || 0}</strong>. This merges all open snapshots into
              claimable allocations and resets the open-point period after on-chain finalize.
            </p>
            <input
              type="number"
              step="0.01"
              placeholder="USDC amount"
              className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
              value={distAmount}
              onChange={(e) => setDistAmount(e.target.value)}
            />
            <input
              placeholder="Label (optional)"
              className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
              value={distLabel}
              onChange={(e) => setDistLabel(e.target.value)}
            />
            <button
              type="button"
              disabled={busy || !distAmount}
              onClick={createAndFinalizeDistribution}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold"
            >
              Create + finalize on-chain
            </button>
          </div>
          <div className="rounded-xl bg-white dark:bg-gray-800 shadow overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left border-b dark:border-gray-700">
                  <th className="p-3">ID</th>
                  <th className="p-3">Amount</th>
                  <th className="p-3">Points</th>
                  <th className="p-3">Claims</th>
                  <th className="p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {distributions.map((d) => (
                  <tr key={d._id} className="border-b dark:border-gray-700">
                    <td className="p-3">#{d.chainDistributionId}</td>
                    <td className="p-3">{formatUsdAmount(d.totalAmountUsd)}</td>
                    <td className="p-3">{d.totalPoints}</td>
                    <td className="p-3">
                      {d.claimedCount}/{d.allocationCount}
                    </td>
                    <td className="p-3">{d.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNftRewardsTab;
